import { inEnum, isBoolean, isNumber, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { MediaAudioOnPolicy, EmbeddablePlayerConfig, isEmbeddablePlayerConfig } from "@mp-commons/config/MediaPlayerConfig.js"
import { DomainConfig } from "@mp-commons/config/DomainConfig.js";
import { VideoPolicyRules } from "@mp-commons/modules/ads/vprules/VideoPolicyRules.js";

/**
 * Configuration options for the Megaride embeddable player.
 *
 * Extends {@link EmbeddablePlayerConfig} with additional properties specific to Megaride's player.
 *
 * @property bar - Whether to display the player title bar.
 * @property viewsPlaceholder - Optional placeholder for the views count.
 * @property authorPlaceholder - Optional placeholder for the author's name.
 * @property titlePlaceholder - Optional placeholder for the media title.
 * @property thumbPlaceholder - Optional placeholder for the thumbnail image.
 * @property usePreferenceQuality - Whether to use the user's preferred quality setting.
 * @property audioOnPolicy - Optional policy for audio playback (see {@link MediaAudioOnPolicy}).
 * @property videoPolicy - Optional rules for video policy (see {@link VideoPolicyRules}).
 * @property manualPlaylist - Whether the playlist navigation must be handled by user.
 * @property embedBehaviour - Whether to enable embed-specific behaviour (es: FANPLAYER MOBILE).
 * @property startingTimecode - Optional starting timecode (in seconds) for the media playback.
 */
export interface MegarideEmbeddablePlayerConfig extends EmbeddablePlayerConfig {
    bar?: boolean,
    viewsPlaceholder?: string,
    authorPlaceholder?: string,
    titlePlaceholder?: string,
    thumbPlaceholder?: string,
    usePreferenceQuality: boolean,
    audioOnPolicy?: MediaAudioOnPolicy,
    videoPolicy?: VideoPolicyRules,
    manualPlaylist: boolean,
    embedBehaviour?: boolean,
    startingTimecode?: number
}


export function isMegarideEmbeddablePlayerConfig(obj: any): obj is MegarideEmbeddablePlayerConfig {
    return isEmbeddablePlayerConfig(obj) &&
        (!hasOwnProperty(obj, 'bar') || isBoolean(obj['bar'])) &&
        hasOwnProperty(obj, 'manualPlaylist') && isBoolean(obj['manualPlaylist']) &&
        (!hasOwnProperty(obj, 'embedBehaviour') || isBoolean(obj['embedBehaviour'])) &&
        hasOwnProperty(obj, 'usePreferenceQuality') && isBoolean(obj['usePreferenceQuality']) &&
        (!hasOwnProperty(obj, 'viewsPlaceholder') || isString(obj['viewsPlaceholder'])) &&
        (!hasOwnProperty(obj, 'authorPlaceholder') || isString(obj['authorPlaceholder'])) &&
        (!hasOwnProperty(obj, 'titlePlaceholder') || isString(obj['titlePlaceholder'])) &&
        (!hasOwnProperty(obj, 'thumbPlaceholder') || isString(obj['thumbPlaceholder'])) &&
        (!hasOwnProperty(obj, 'audioOnPolicy') || inEnum(obj['audioOnPolicy'], MediaAudioOnPolicy)) &&
        (!hasOwnProperty(obj, 'videoPolicy') || inEnum(obj['videoPolicy'], VideoPolicyRules)) &&
        (!hasOwnProperty(obj, 'startingTimecode') || isNumber(obj['startingTimecode']));

}



/**
 * Configuration options for the Megaride player.
 *
 * @property domain - The domain-specific configuration for the player.
 * @property embeddablePlayerConfig - Optional configuration for embedding the player.
 */
export interface MegaridePlayerConfig {
    domain: DomainConfig,
    embeddablePlayerConfig?: MegarideEmbeddablePlayerConfig

}

export type ComscoreLoadMode = "jit" | "lazy" | "preload"

/**
 * Extends `MegaridePlayerConfig` to provide additional configuration options for the Parthenope player.
 *
 * @property prebid - Configuration for Prebid integration.
 *   @property enable - Whether Prebid is enabled.
 *   @property amazon - Optional Amazon publisher configuration.
 *     @property pubID - Amazon publisher ID.
 *   @property ias - Optional IAS configuration.
 *     @property pubId - IAS publisher ID.
 *     @property anId - IAS account number ID.
 *   @property timeout - Prebid timeout in milliseconds.
 * @property reuseExistingVideoElement - If true, reuses an existing video element instead of creating a new one.
 * @property initialPoster - URL of the initial poster image to display before playback.
 * @property comscoreLoadMode - Optional mode for loading Comscore analytics.
 * @property onlyVideoPoster - If true, displays only the video poster without additional overlays.
 * @property enableStoryboard - If true, enables storyboard previews for the video.
 */
export interface ParthenopePlayerConfig extends MegaridePlayerConfig {
    prebid?: {
        enable: boolean,
        amazon?: {
            pubID: string
        },
        ias?: {
            pubId: string,
            anId: string
        }
        timeout: number
    },
    reuseExistingVideoElement?: boolean,
    initialPoster: string,
    comscoreLoadMode?: ComscoreLoadMode,
    onlyVideoPoster: boolean,
    enableStoryboard: boolean,
}

/**
 * Extends `MegaridePlayerConfig` to provide additional configuration options specific to the Ligea player.
 *
 * @property initialPoster - The URL or path to the initial poster image displayed before playback starts.
 * @property availableContent - Specifies if the content is available for playback.
 */
export interface LigeaPlayerConfig extends MegaridePlayerConfig {
    initialPoster: string,
    availableContent: boolean
}

/**
 * Extends `MegaridePlayerConfig` to provide additional configuration options for the Leucosia player.
 *
 * @property initialPoster - The URL of the initial poster image to display before playback starts.
 * @property reuseExistingVideoElement - Optional flag indicating whether to reuse an existing video element instead of creating a new one.
 */
export interface LeucosiaPlayerConfig extends MegaridePlayerConfig {
    initialPoster: string,
    reuseExistingVideoElement?: boolean,
}